import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Loader2, XCircle, Gift } from "lucide-react";
import { useLang } from "../i18n";
import { api } from "../lib/api";
import OliveBranch from "../components/OliveBranch";

const MAX_TRIES = 20;

export default function VoucherSuccess() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const s = t.vouchers.success;
  const sessionId = params.get("session_id");

  const [state, setState] = useState("checking"); // checking | paid | failed
  const [data, setData] = useState(null);

  useEffect(() => {
    let tries = 0;
    let cancelled = false;
    if (!sessionId) {
      setState("failed");
      return;
    }
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setData(res.data);
          setState("paid");
          return;
        }
      } catch (e) {
        /* keep trying */
      }
      tries += 1;
      if (tries >= MAX_TRIES) {
        if (!cancelled) setState("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="bg-qs-bg min-h-screen flex items-center justify-center px-5">
      <div className="max-w-lg w-full text-center py-24" data-testid="voucher-success-page">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="text-qs-gold mx-auto animate-spin" />
            <p className="font-outfit text-qs-text2 mt-6">{s.checking}</p>
          </>
        )}

        {state === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-qs-gold/15 flex items-center justify-center mx-auto">
              <Check size={40} className="text-qs-gold" />
            </div>
            <h1 className="font-cormorant text-qs-text text-4xl sm:text-5xl mt-6">{s.title}</h1>
            <OliveBranch className="w-24 text-qs-gold my-5 mx-auto" />
            <p className="font-outfit text-qs-text2">{s.subtitle}</p>

            <div className="mt-8 bg-qs-bg2 border border-qs-border rounded-2xl p-6">
              <p className="font-outfit text-qs-text2 text-xs uppercase tracking-widest mb-2">{s.codeLabel}</p>
              <p className="font-cormorant font-bold text-qs-gold text-3xl tracking-[0.15em]" data-testid="voucher-code">
                {data?.code || "—"}
              </p>
              <p className="font-cormorant text-qs-text text-2xl mt-3">{data?.amount ? `${data.amount} €` : ""}</p>
            </div>
            <p className="flex items-center justify-center gap-2 font-outfit text-qs-text2 text-sm mt-5">
              <Gift size={14} className="text-qs-gold" /> {s.emailNote}
            </p>

            <button
              onClick={() => navigate("/")}
              data-testid="voucher-success-home"
              className="mt-8 inline-flex items-center bg-qs-red text-white rounded-full px-8 py-4 text-xs font-semibold tracking-[0.14em] uppercase hover:bg-qs-reddark transition-colors"
            >
              {s.home}
            </button>
          </>
        )}

        {state === "failed" && (
          <>
            <XCircle size={48} className="text-qs-red mx-auto" />
            <h1 className="font-cormorant text-qs-text text-3xl mt-6">{s.failedTitle}</h1>
            <p className="font-outfit text-qs-text2 mt-3">{s.failedNote}</p>
            <div className="flex items-center justify-center gap-3 mt-8">
              <button
                onClick={() => navigate("/gutscheine")}
                data-testid="voucher-retry"
                className="inline-flex items-center bg-qs-gold text-black rounded-full px-7 py-3.5 text-xs font-semibold tracking-[0.12em] uppercase hover:bg-qs-gold/90 transition-colors"
              >
                {s.retry}
              </button>
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center border border-qs-border text-qs-text rounded-full px-7 py-3.5 text-xs font-semibold tracking-[0.12em] uppercase hover:border-qs-gold transition-colors"
              >
                {s.home}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
